import { ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import storage from '@skynet/frontend-shell/dist/legacy/storage';
import type { Message, StreamEvent } from '@/types/chat';

function getAuthHeader(): string {
    const auth = storage.get('app_auth') as { access_token?: string; token_type?: string };
    return auth.access_token ? `${auth.token_type ?? 'Bearer'} ${auth.access_token}` : '';
}

export function useChat(apiUrl: string) {
    const { t } = useI18n();
    const messages = ref<Message[]>([]);
    const isStreaming = ref(false);
    const isThinking = ref(false);

    let privacyKey: string | null = null;
    let sessionId: string | null = null;
    let abortController: AbortController | null = null;

    function getOrCreatePrivacyKey(): string {
        if (!privacyKey) privacyKey = crypto.randomUUID();
        return privacyKey;
    }

    function pushError(text: string): void {
        messages.value.push({ id: crypto.randomUUID(), role: 'error', content: text });
    }

    async function sendMessage(prompt: string): Promise<void> {
        if (isStreaming.value) return;

        messages.value.push({ id: crypto.randomUUID(), role: 'user', content: prompt });
        isStreaming.value = true;
        isThinking.value = true;
        abortController = new AbortController();

        const authHeader = getAuthHeader();
        let response: Response;
        try {
            response = await fetch(`${apiUrl}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authHeader ? { Authorization: authHeader } : {}),
                },
                body: JSON.stringify({
                    prompt,
                    privacy_session_key: getOrCreatePrivacyKey(),
                    ...(sessionId ? { session_id: sessionId } : {}),
                }),
                signal: abortController.signal,
            });
        } catch (err) {
            if ((err as Error).name === 'AbortError') {
                isStreaming.value = false;
                isThinking.value = false;
                return;
            }
            pushError(t('chat.error.connectionFailed'));
            isStreaming.value = false;
            isThinking.value = false;
            return;
        }

        if (!response.ok) {
            pushError(
                response.status === 401
                    ? t('chat.error.unauthorized')
                    : t('chat.error.httpError', { status: response.status }),
            );
            isStreaming.value = false;
            isThinking.value = false;
            return;
        }

        if (!response.body) {
            isStreaming.value = false;
            isThinking.value = false;
            return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let assistantMsgId: string | null = null;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (value) buffer += decoder.decode(value, { stream: true });
                if (done) buffer += decoder.decode(); // flush multi-byte state

                const lines = buffer.split('\n');
                // On done, process every line including the last one (no trailing \n needed).
                // Otherwise save the last incomplete line for the next chunk.
                buffer = done ? '' : lines.pop()!;

                for (const line of lines) {
                    if (!line.trim()) continue;

                    let event: StreamEvent;
                    try {
                        event = JSON.parse(line);
                    } catch {
                        continue;
                    }

                    if (event.event_type === 'metadata' && event.metadata?.session_id) {
                        sessionId = event.metadata.session_id;
                    }

                    if (event.event_type === 'ai_message_chunk') {
                        isThinking.value = false;
                        if (!assistantMsgId) {
                            assistantMsgId = crypto.randomUUID();
                            messages.value.push({
                                id: assistantMsgId,
                                role: 'assistant',
                                content: event.data ?? '',
                            });
                        } else {
                            const msg = messages.value.find(
                                (m: Message) => m.id === assistantMsgId,
                            );
                            if (msg) msg.content += event.data ?? '';
                        }
                    } else if (event.event_type === 'ai_message') {
                        isThinking.value = false;
                        if (assistantMsgId) {
                            // Chunks already built the message — replace with the final complete version.
                            const msg = messages.value.find(
                                (m: Message) => m.id === assistantMsgId,
                            );
                            if (msg) msg.content = event.data ?? '';
                        } else {
                            // Non-streaming response: no chunks arrived, add the full message.
                            messages.value.push({
                                id: crypto.randomUUID(),
                                role: 'assistant',
                                content: event.data ?? '',
                            });
                        }
                    } else if (event.event_type === 'tool_call') {
                        isThinking.value = true;
                    }
                    // log_message and error intentionally ignored
                }

                if (done) break;
            }
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                pushError(t('chat.error.streamInterrupted'));
            }
        } finally {
            isStreaming.value = false;
            isThinking.value = false;
            abortController = null;
        }
    }

    function abortStream(): void {
        abortController?.abort();
    }

    function resetConversation(): void {
        abortController?.abort();
        messages.value = [];
        privacyKey = null;
        sessionId = null;
        isStreaming.value = false;
        isThinking.value = false;
    }

    onUnmounted(() => {
        abortController?.abort();
    });

    return { messages, isStreaming, isThinking, sendMessage, abortStream, resetConversation };
}
